import { Organism } from './organism'
import { Food } from './food'
import { DietType, getDietType, type Obstacle, type PopSnapshot, type PopulationConfig } from './types'

const INITIAL_FOOD = 70
const MAX_FOOD = 180               // 食料上限を調整し、草食の安定個体数を確保
const FOOD_SPAWN_INTERVAL = 0.35   // 秒（獲物密度を維持して肉食の狩りを成立させる）
const MAX_ORGANISMS = 150          // 仕様I: 個体数上限緩和（100→150）
const OBSTACLE_COUNT = 6
/** 基準繁殖エネルギー（全食性で繁殖サイクルを緩やかに） */
const BASE_REPRODUCTION_ENERGY = 120
const REPRODUCTION_COST = 70
/** 世代記録・グラフ更新の間隔（tick単位） */
const SNAPSHOT_INTERVAL = 60
/** 保存するスナップショット数 */
const MAX_SNAPSHOTS = 300

export class World {
  width: number
  height: number
  organisms: Organism[]
  foods: Food[]
  obstacles: Obstacle[]

  private foodTimer = 0
  private soilFertility = 1.0  // 0.0（荒廃）〜 1.0（豊穣）
  tick = 0
  maxGeneration = 0

  /** 個体数の時系列履歴 */
  popHistory: PopSnapshot[] = []
  private snapshotTimer = 0

  constructor(width: number, height: number, popConfig?: PopulationConfig) {
    this.width = width
    this.height = height
    this.organisms = []
    this.foods = []
    this.obstacles = []
    this.init(popConfig)
  }

  private init(popConfig?: PopulationConfig): void {
    for (let i = 0; i < OBSTACLE_COUNT; i++) {
      this.obstacles.push({
        pos: {
          x: (0.1 + Math.random() * 0.8) * this.width,
          y: (0.1 + Math.random() * 0.8) * this.height,
        },
        radius: 20 + Math.random() * 25,
      })
    }

    if (popConfig) {
      this.spawnByDiet(DietType.Herbivore, popConfig.herbivores)
      this.spawnByDiet(DietType.Carnivore, popConfig.carnivores)
      this.spawnByDiet(DietType.Omnivore, popConfig.omnivores)
    } else {
      // デフォルト: 70体をランダム配分
      for (let i = 0; i < 70; i++) {
        const pos = this.safePosition()
        this.organisms.push(new Organism(pos.x, pos.y))
      }
    }

    for (let i = 0; i < INITIAL_FOOD; i++) {
      this.spawnFood()
    }
  }

  private spawnByDiet(diet: DietType, count: number): void {
    for (let i = 0; i < count; i++) {
      const pos = this.safePosition()
      const org = new Organism(pos.x, pos.y)
      if (diet === DietType.Herbivore) {
        org.traits.dietScore = 0.75 + Math.random() * 0.25   // 0.75〜1.0
        org.traits.hue = 90 + Math.random() * 60
        org.traits.speed = 1.05 + Math.random() * 0.9
        org.traits.size = 3 + Math.random() * 3
      } else if (diet === DietType.Carnivore) {
        org.traits.dietScore = Math.random() * 0.25           // 0.0〜0.25
        org.traits.hue = Math.random() * 30
        org.traits.speed = 1.75 + Math.random() * 1.5
        org.traits.size = 5 + Math.random() * 4
      } else {
        org.traits.dietScore = 0.35 + Math.random() * 0.30   // 0.35〜0.65
        org.traits.hue = 200 + Math.random() * 80
        org.traits.speed = 1.4 + Math.random() * 1.2
        org.traits.size = 3 + Math.random() * 3
        org.traits.fertilityRate = 0.6 + Math.random() * 0.3
      }
      this.organisms.push(org)
    }
  }

  private safePosition(): { x: number; y: number } {
    for (let attempt = 0; attempt < 20; attempt++) {
      const x = Math.random() * this.width
      const y = Math.random() * this.height
      let ok = true
      for (const obs of this.obstacles) {
        const dx = x - obs.pos.x
        const dy = y - obs.pos.y
        if (Math.sqrt(dx * dx + dy * dy) < obs.radius + 10) { ok = false; break }
      }
      if (ok) return { x, y }
    }
    return { x: Math.random() * this.width, y: Math.random() * this.height }
  }

  update(dt: number): void {
    this.tick++

    for (const org of this.organisms) {
      if (org.alive) {
        org.update(dt, this.foods, this.organisms, this.width, this.height, this.obstacles)
      }
    }

    this.organisms = this.organisms.filter(o => o.alive)
    const foodBefore = this.foods.length
    this.foods = this.foods.filter(f => f.alive)
    const foodConsumed = foodBefore - this.foods.length

    // 土壌肥沃度の更新（回復を速く、枯渇を緩やかに）
    this.soilFertility += (1.0 - this.soilFertility) * 0.008  // 仕様H: 回復率減速（0.015→0.008）
    this.soilFertility -= foodConsumed * 0.012  // 仕様G: 土壌枯渇係数倍増（0.006→0.012）
    this.soilFertility = Math.max(0, Math.min(1, this.soilFertility))

    // 荒廃ペナルティ: 肥沃度30%未満で全個体に環境ストレス
    if (this.soilFertility < 0.4) {
      const stressFactor = (0.4 - this.soilFertility) / 0.4 // 仕様J: 荒廃閾値引き上げ（0.3→0.4）
      const drain = stressFactor * 0.06                       // 最大 0.06/frame = 3.6/real-sec
      for (const org of this.organisms) {
        org.energy -= drain
        if (org.energy <= 0) org.alive = false
      }
      this.organisms = this.organisms.filter(o => o.alive)
    }

    // 繁殖（個体ごとの fertilityRate で閾値が変わる）
    const toAdd: Organism[] = []
    for (const org of this.organisms) {
      const threshold = BASE_REPRODUCTION_ENERGY / org.traits.fertilityRate
      // 誕生クールダウン中は繁殖不可（再繁殖クールダウンはなし）
      if (org.energy >= threshold && org.birthCooldown === 0 && this.organisms.length + toAdd.length < MAX_ORGANISMS) {
        // 変更4: 連続繁殖コスト（特化ほど低コスト、雑食は両消化器官を維持するため高コスト）
        const specialization = Math.abs(2 * org.traits.dietScore - 1)
        const reproductionCost = REPRODUCTION_COST * (1.2 - specialization * 0.3)
        // 雑食(spec=0): 84エネルギー, 純草食/純肉食(spec=1): 63エネルギー
        org.energy -= reproductionCost
        const offspring = this.reproduce(org)
        toAdd.push(offspring)
        if (offspring.generation > this.maxGeneration) {
          this.maxGeneration = offspring.generation
        }
      }
    }
    this.organisms.push(...toAdd)

    // 完全絶滅後の再スタート（全個体が死滅した場合のみ草食をスポーン、約5秒の遅延あり）
    // 生態学的根拠: 絶滅後の再定着はr戦略種（草食）から始まる
    if (this.organisms.length === 0 && this.tick % 300 === 0) {
      for (let i = 0; i < 6; i++) {
        const pos = this.safePosition()
        const org = new Organism(pos.x, pos.y)
        org.traits.dietScore = 0.75 + Math.random() * 0.25
        org.traits.hue = 90 + Math.random() * 60
        org.traits.speed = 1.0 + Math.random() * 0.8
        org.traits.size = 3 + Math.random() * 2
        this.organisms.push(org)
      }
    }

    // 草食の絶滅防止（セーフティネット: 2体未満で発動、約10秒間隔）
    const herbivores = this.organisms.filter(o => o.traits.dietScore >= 0.7).length
    if (herbivores < 2 && this.tick % 600 === 0) {
      for (let i = 0; i < 2; i++) {
        const pos = this.safePosition()
        const org = new Organism(pos.x, pos.y)
        org.traits.dietScore = 0.75 + Math.random() * 0.25
        org.traits.hue = 90 + Math.random() * 60
        org.traits.speed = 1.2 + Math.random() * 0.8
        org.traits.size = 3 + Math.random() * 2
        this.organisms.push(org)
      }
    }

    // 食料スポーン（土壌肥沃度による変調: 豊穣時は正常、荒廃時は急停止）
    this.foodTimer += dt
    if (this.foodTimer >= FOOD_SPAWN_INTERVAL) {
      this.foodTimer = 0
      if (this.foods.length < MAX_FOOD) {
        const baseN = Math.floor(Math.random() * 5) + 3
        // 豊穣（≥0.7）: 正常スポーン / 0.35〜0.7: 線形減少 / 荒廃（≤0.35）: ほぼ停止
        const fertFactor = this.soilFertility >= 0.7 ? 1.0
          : this.soilFertility <= 0.35 ? 0.0
          : (this.soilFertility - 0.35) / 0.35
        const n = Math.round(baseN * fertFactor)
        for (let i = 0; i < n; i++) this.spawnFood()
      }
    }

    // 個体数スナップショット（グラフ用）
    this.snapshotTimer++
    if (this.snapshotTimer >= SNAPSHOT_INTERVAL) {
      this.snapshotTimer = 0
      const snap = this.currentSnapshot()
      this.popHistory.push(snap)
      if (this.popHistory.length > MAX_SNAPSHOTS) {
        this.popHistory.shift()
      }
    }
  }

  private currentSnapshot(): PopSnapshot {
    let herbivores = 0
    let carnivores = 0
    let omnivores = 0
    for (const o of this.organisms) {
      const diet = getDietType(o.traits.dietScore)
      if (diet === DietType.Herbivore) herbivores++
      else if (diet === DietType.Carnivore) carnivores++
      else omnivores++
    }
    return { tick: this.tick, herbivores, carnivores, omnivores }
  }

  private reproduce(parent: Organism): Organism {
    const pt = parent.traits
    const mutate = (v: number, range: number) => v + (Math.random() - 0.5) * range
    const newDietScore = Math.max(0, Math.min(1, mutate(pt.dietScore, 0.12)))
    const offspring = new Organism(
      parent.pos.x + (Math.random() - 0.5) * 20,
      parent.pos.y + (Math.random() - 0.5) * 20,
      {
        speed: Math.max(0.3, mutate(pt.speed, 0.4)),
        visionRange: Math.max(20, mutate(pt.visionRange, 10)),
        size: Math.max(2, mutate(pt.size, 1)),
        dietScore: newDietScore,
        hue: (pt.hue + (Math.random() - 0.5) * 20 + 360) % 360,
        metabolismRate: Math.max(0.5, mutate(pt.metabolismRate, 0.15)),
        fertilityRate: Math.max(0.4, Math.min(2.0, mutate(pt.fertilityRate, 0.1))),
      },
      parent.generation + 1,
      parent.id,
    )
    // 誕生クールダウン: 生まれた直後は3〜5秒間繁殖不可（花火バグ防止）
    offspring.birthCooldown = 180 + Math.floor(Math.random() * 120)
    return offspring
  }

  private spawnFood(): void {
    const pos = this.safePosition()
    this.foods.push(new Food(pos.x, pos.y, 20 + Math.random() * 20))
  }

  get stats() {
    const snap = this.currentSnapshot()
    return {
      organisms: this.organisms.length,
      foods: this.foods.length,
      tick: this.tick,
      herbivores: snap.herbivores,
      carnivores: snap.carnivores,
      omnivores: snap.omnivores,
      maxGeneration: this.maxGeneration,
      soilFertility: Math.round(this.soilFertility * 100) / 100,
    }
  }
}
